import React, { useEffect, useRef } from 'react';
import LoadingSpinner from './LoadingSpinner';
import { acquireScrollLock, releaseScrollLock } from '../utils/scrollLock';

/**
 * LoadingOverlay
 * ----------------
 * Versatile overlay or inline loader with unified styling.
 *
 * Props
 *  - fullScreen  : boolean  – If true, stretches across viewport
 *  - message     : string   – Optional loading text displayed under spinner
 *  - size        : string   – Spinner size (xs | sm | md | lg | xl)
 *  - className   : string   – Extra classes for outer container (useful for inline loader)
 */
const LoadingOverlay = ({ fullScreen = false, message = 'Loading...', size = 'md', className = '' }) => {
  const containerClasses = fullScreen
    ? 'fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/70 backdrop-blur-sm pointer-events-auto'
    : 'flex flex-col items-center justify-center';

  // Prevent scrolling when fullscreen overlay is active. Routed through the
  // shared scroll-lock refcount so we cooperate with Modal / bottom-sheets
  // instead of stomping on each other's `body.style.overflow`. Padding-right
  // compensation is preserved with a save/restore guarded by a ref so it
  // survives StrictMode effect replays.
  const lockHeldRef = useRef(false);
  const originalPaddingRightRef = useRef('');
  useEffect(() => {
    if (!fullScreen) return undefined;

    if (!lockHeldRef.current) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      originalPaddingRightRef.current = document.body.style.paddingRight;
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      acquireScrollLock();
      lockHeldRef.current = true;
    }

    return () => {
      if (lockHeldRef.current) {
        releaseScrollLock();
        document.body.style.paddingRight = originalPaddingRightRef.current;
        lockHeldRef.current = false;
      }
    };
  }, [fullScreen]);

  return (
    <div 
      className={`${containerClasses} ${className}`}
      style={fullScreen ? { pointerEvents: 'all' } : undefined}
      onClick={(e) => fullScreen && e.stopPropagation()}
    >
      <LoadingSpinner size={size} />
      {message && <p className="mt-3 text-sm text-primary animate-pulse">{message}</p>}
    </div>
  );
};

export default LoadingOverlay; 